import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiClient, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import type {
  RawComponent,
  RawQuestionStats,
  RawSectionStats,
} from '@/features/content/useComponent';

import type {
  AssignmentScore,
  CoachUnitScore,
  GradebookAssignment,
  GradebookQuestionMeta,
  GradebookRoster,
  GradebookStudent,
  QuestionScore,
  StudentGradesState,
} from './gradebookTypes';
import {
  buildQuestionMetaKey,
  formatGradeValue,
  sumQuestionScores,
} from './gradebookTypes';

interface RawKuduUserProfile {
  first_name?: string | null;
  firstName?: string | null;
  institute_official_id?: string | null;
  instituteOfficialId?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  preferred_email?: string | null;
  preferred_username?: string | null;
  preferredEmail?: string | null;
  preferredUsername?: string | null;
  [key: string]: unknown;
}

interface RawCourseStudent {
  components?: RawComponent[] | null;
  component_ids?: Array<number | string> | null;
  componentIds?: Array<number | string> | null;
  id?: string | null;
  profile?: RawKuduUserProfile | null;
  total_stats?: RawSectionStats | null;
  totalStats?: RawSectionStats | null;
  uid?: string | null;
}

interface RawCourseStudentGroup {
  course_student_ids?: string[] | null;
  course_student_group?: RawCourseStudentGroup | null;
  course_students?: RawCourseStudent[] | null;
  courseStudentIds?: string[] | null;
  courseStudents?: RawCourseStudent[] | null;
}

interface RawGetCourseStudentGroupResponse {
  course_student_group?: RawCourseStudentGroup | null;
  courseStudentGroup?: RawCourseStudentGroup | null;
}

interface RawGetCourseStudentsResponse {
  course_students?: RawCourseStudent[] | null;
  courseStudents?: RawCourseStudent[] | null;
  end_index?: number | string | null;
  endIndex?: number | string | null;
  start_index?: number | string | null;
  startIndex?: number | string | null;
}

// An instructor's scores for some of an assignment's questions (question meta
// key → score). Only the questions that changed are passed; each becomes
// instructor-adjusted.
export interface SaveGradeEditParams {
  assignment: GradebookAssignment;
  courseId: string;
  editedQuestionScores: Record<string, number>;
  student: GradebookStudent;
}

// An instructor's scores for some of a coach's units (unit uid → score). Only
// the units that changed are sent; each becomes instructor-adjusted.
export interface SaveCoachScoresParams {
  assignment: GradebookAssignment;
  courseId: string;
  scores: Record<string, number>;
  student: GradebookStudent;
}

// One question of an assignment, for an action on the whole class: give every
// student full credit for it, or remove it from (or restore it to) the
// assignment.
export interface QuestionClassActionParams {
  assignment: GradebookAssignment;
  courseId: string;
  question: GradebookQuestionMeta;
}

export interface SetQuestionRemovedParams extends QuestionClassActionParams {
  removed: boolean;
}

export interface GradebookService {
  getRoster(courseId: string): Promise<GradebookRoster>;
  getStudentGrades(
    courseId: string,
    startIndex: number,
    endIndex: number,
    componentIds: string[],
    assignments: GradebookAssignment[],
  ): Promise<GradebookStudent[]>;
  saveGradeEdit(params: SaveGradeEditParams): Promise<void>;
  saveCoachUnitScores(params: SaveCoachScoresParams): Promise<void>;
  giveQuestionFullCredit(params: QuestionClassActionParams): Promise<void>;
  setQuestionRemoved(params: SetQuestionRemovedParams): Promise<void>;
}

export const DEFAULT_GRADEBOOK_BATCH_SIZE = 100;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : undefined;
}

function unwrapCourseStudentGroupResponse(
  response:
    | RawGetCourseStudentGroupResponse
    | LegacyKuduResponseEnvelope<RawGetCourseStudentGroupResponse>
    | null
    | undefined,
): RawGetCourseStudentGroupResponse | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapCourseStudentGroupResponse(
      response.Response as RawGetCourseStudentGroupResponse,
    );
  }

  return isRecord(response) ? (response as RawGetCourseStudentGroupResponse) : null;
}

function unwrapCourseStudentsResponse(
  response:
    | RawGetCourseStudentsResponse
    | LegacyKuduResponseEnvelope<RawGetCourseStudentsResponse>
    | null
    | undefined,
): RawGetCourseStudentsResponse | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapCourseStudentsResponse(
      response.Response as RawGetCourseStudentsResponse,
    );
  }

  return isRecord(response) ? (response as RawGetCourseStudentsResponse) : null;
}

function toComponentArray(value: unknown): RawComponent[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawComponent[];
}

function toCourseStudentArray(value: unknown): RawCourseStudent[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawCourseStudent[];
}

function toQuestionStatsArray(value: unknown): RawQuestionStats[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestionStats[];
}

function toSectionStatsArray(value: unknown): RawSectionStats[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSectionStats[];
}

function getProfileField(
  profile: RawKuduUserProfile | null | undefined,
  snakeKey: keyof RawKuduUserProfile,
  camelKey: keyof RawKuduUserProfile,
) {
  return normalizeString(profile?.[snakeKey]) ?? normalizeString(profile?.[camelKey]);
}

function createEmptyStudentTotals(assignments: GradebookAssignment[]) {
  return assignments.reduce(
    (totals, assignment) => ({
      totalHomeworkPossible:
        totals.totalHomeworkPossible +
        (assignment.isSectionDivider ? 0 : assignment.homeworkMaxScore),
      totalParticipationPossible:
        totals.totalParticipationPossible +
        (assignment.isSectionDivider ? 0 : assignment.participationMaxScore),
      totalPreLecturePossible:
        totals.totalPreLecturePossible +
        (assignment.isSectionDivider ? 0 : assignment.preLectureMaxScore),
    }),
    {
      totalHomeworkPossible: 0,
      totalParticipationPossible: 0,
      totalPreLecturePossible: 0,
    },
  );
}

export function createRosterStudent(rawStudent: RawCourseStudent): GradebookStudent {
  const profile = isRecord(rawStudent.profile)
    ? (rawStudent.profile as RawKuduUserProfile)
    : null;
  const id =
    normalizeString(rawStudent.id) ??
    normalizeString(rawStudent.uid) ??
    'student';
  const email =
    getProfileField(profile, 'preferred_email', 'preferredEmail') ??
    id;
  const firstName =
    getProfileField(profile, 'first_name', 'firstName') ?? '';
  const lastName =
    getProfileField(profile, 'last_name', 'lastName') ?? '';
  const instituteId =
    getProfileField(profile, 'institute_official_id', 'instituteOfficialId') ?? '';
  const preferredUsername =
    getProfileField(profile, 'preferred_username', 'preferredUsername') ?? '';

  return {
    assignmentScores: {},
    email,
    firstName,
    hasLoadedGrades: false,
    id,
    instituteId,
    lastName,
    profile: {
      firstName,
      instituteId,
      lastName,
      preferredEmail: email,
      preferredUsername,
      rawProfile: profile ? { ...profile } : null,
    },
    totalHomeworkPossible: 0,
    totalHomeworkScore: 0,
    totalParticipationPossible: 0,
    totalParticipationScore: 0,
    totalPreLecturePossible: 0,
    totalPreLectureScore: 0,
    ...(normalizeString(rawStudent.uid) ? { uid: normalizeString(rawStudent.uid)! } : {}),
  };
}

// A roster student known only by id: their name, email and scores come with
// their grades.
function createRosterStudentFromId(id: string): GradebookStudent {
  return {
    ...createRosterStudent({ id }),
    email: '',
    profile: {
      firstName: '',
      instituteId: '',
      lastName: '',
      preferredEmail: '',
      preferredUsername: '',
      rawProfile: null,
    },
  };
}

/**
 * The roster. The backend stores it as its students' ids
 * (`course_student_ids`), and GetCourseStudentGroup returns the group as
 * stored, whatever include_student_details says, so it names students by id
 * alone. Each id is a student; GetCourseStudents slices these same ids for the
 * grades. A profile listed for an id is kept, and a group that lists students
 * but no ids is read as it lists them.
 *
 * An id listed twice is one student: the roster's add before #80 appended
 * without checking. The count stays the stored list's, since the grades are
 * fetched by position in it and every position must be asked for.
 */
function readRoster(group: RawCourseStudentGroup | null): GradebookRoster {
  const listed = toCourseStudentArray(group?.course_students ?? group?.courseStudents);
  const rawIds = group?.course_student_ids ?? group?.courseStudentIds;
  const ids = (Array.isArray(rawIds) ? rawIds : [])
    .map((id) => normalizeString(id))
    .filter((id): id is string => id !== null);

  if (ids.length === 0) {
    const students = listed.map((student) => createRosterStudent(student));
    return { studentCount: students.length, students };
  }

  const students = [...new Set(ids)].map((id) => {
    const entry = listed.find(
      (student) => normalizeString(student.id) === id || normalizeString(student.uid) === id,
    );
    return entry ? createRosterStudent(entry) : createRosterStudentFromId(id);
  });
  return { studentCount: ids.length, students };
}

export function createGradebookBatchRanges(
  studentCount: number,
  batchSize = DEFAULT_GRADEBOOK_BATCH_SIZE,
) {
  if (studentCount <= 0) {
    return [];
  }

  const ranges: Array<{ endIndex: number; startIndex: number }> = [];

  for (let startIndex = 0; startIndex < studentCount; startIndex += batchSize) {
    ranges.push({
      endIndex: Math.min(studentCount, startIndex + batchSize),
      startIndex,
    });
  }

  return ranges;
}

function findRawComponent(
  rawComponents: RawComponent[],
  assignment: GradebookAssignment,
) {
  return rawComponents.find((component) => {
    const componentUid = normalizeIdentifier(component.uid);
    const componentId = normalizeIdentifier(component.id);

    return (
      componentUid === assignment.componentUid ||
      componentUid === assignment.componentId ||
      componentId === assignment.componentUid ||
      componentId === assignment.componentId
    );
  });
}

function createQuestionScore(
  rawQuestionStat: RawQuestionStats | null,
  meta: GradebookAssignment['sections'][number]['homeworkQuestions'][number],
  sectionStatsId: string,
): QuestionScore {
  return {
    adjusted:
      (rawQuestionStat?.instructor_adjusted ?? rawQuestionStat?.instructorAdjusted) === true,
    attempts:
      normalizeNumber(
        rawQuestionStat?.current_num_attempts ?? rawQuestionStat?.currentNumAttempts,
      ) ?? 0,
    id: normalizeIdentifier(rawQuestionStat?.id) ?? meta.id,
    index: meta.index,
    kind: meta.kind,
    label: meta.label,
    maxScore: meta.maxScore,
    needsGrading:
      normalizeBoolean(rawQuestionStat?.needs_grading ?? rawQuestionStat?.needsGrading) ??
      false,
    score: normalizeNumber(rawQuestionStat?.score) ?? null,
    sectionId: meta.sectionId,
    sectionStatsId,
  };
}

function getSectionTotal(
  rawTotal: unknown,
  questionScores: QuestionScore[],
) {
  const normalizedTotal = normalizeNumber(rawTotal);

  if (normalizedTotal !== undefined) {
    return normalizedTotal;
  }

  return questionScores.some((question) => question.score !== null)
    ? sumQuestionScores(questionScores)
    : null;
}

/**
 * The server's section-id rule (normalizeSectionStatID in
 * backend/app/course-student-merge.go): a component copied from another course
 * keeps that course's 6-character prefix in its section ids, while a student's
 * stats are stored under the live course's prefix.
 */
export function normalizeSectionStatId(sectionId: string, courseId: string | undefined): string {
  if (
    courseId &&
    courseId.length === 6 &&
    sectionId.length > 6 &&
    sectionId.slice(0, 6) !== courseId
  ) {
    return courseId + sectionId.slice(6);
  }
  return sectionId;
}

/**
 * Pairs a student's section stats with the assignment's sections: the stat
 * named after the section (ids normalised to the course), as the student save
 * and grading pair them; otherwise the stat at the section's position, unless
 * that stat is named after another of the assignment's sections, which would
 * count its scores twice. Stats were read by position alone, so a student whose
 * stored stats did not line up with the chapter's sections showed no scores or
 * the wrong ones.
 */
function pairSectionStats(
  sectionStats: RawSectionStats[],
  sections: GradebookAssignment['sections'],
  courseId: string | undefined,
): Array<RawSectionStats | null> {
  const sectionIds = sections.map((section) => normalizeSectionStatId(section.id, courseId));
  const statIds = sectionStats.map((stats) => {
    const id = normalizeIdentifier(stats?.id);
    return id ? normalizeSectionStatId(id, courseId) : null;
  });

  return sections.map((section, sectionPosition) => {
    const byId = statIds.findIndex((id) => id !== null && id === sectionIds[sectionPosition]);
    if (byId >= 0) {
      return sectionStats[byId] ?? null;
    }
    const positionalId = statIds[section.index] ?? null;
    if (positionalId !== null && sectionIds.includes(positionalId)) {
      return null;
    }
    return sectionStats[section.index] ?? null;
  });
}

function normalizeAssignmentScore(
  assignment: GradebookAssignment,
  rawComponent: RawComponent | null,
  courseId?: string,
): AssignmentScore | null {
  if (!rawComponent || assignment.isSectionDivider) {
    return null;
  }

  const sectionStats = toSectionStatsArray(
    rawComponent.section_stats ?? rawComponent.sectionStats,
  );

  // Kai Coach participation: scores are stored one SectionStats per scorable
  // unit (keyed by unit uid), not positionally per core section. Sum the earned
  // points across units; do NOT run the question/section path (which would
  // misread the per-unit stats as a homework total).
  if (assignment.hasParticipation) {
    const anyScored = sectionStats.some(
      (stat) => normalizeNumber(stat?.total_score ?? stat?.totalScore) !== undefined,
    );
    const coachUnitScores: CoachUnitScore[] = (assignment.coachUnits ?? []).map((unit) => {
      const stat = sectionStats.find((candidate) => normalizeIdentifier(candidate?.id) === unit.uid);
      return {
        adjusted: (stat?.instructor_adjusted ?? stat?.instructorAdjusted) === true,
        score: normalizeNumber(stat?.total_score ?? stat?.totalScore) ?? null,
        unitUid: unit.uid,
      };
    });
    return {
      coachUnitScores,
      componentId: assignment.componentId,
      componentTitle: assignment.title,
      componentUid: assignment.componentUid,
      homeworkPossible: 0,
      homeworkScore: null,
      participationPossible: assignment.participationMaxScore,
      participationScore: anyScored
        ? sectionStats.reduce(
            (total, stat) =>
              total + (normalizeNumber(stat?.total_score ?? stat?.totalScore) ?? 0),
            0,
          )
        : null,
      preLecturePossible: 0,
      preLectureQuestionScores: [],
      preLectureScore: null,
      questionScores: [],
      sectionScores: [],
    };
  }
  const pairedStats = pairSectionStats(sectionStats, assignment.sections, courseId);
  const sectionScores = assignment.sections.map((section, sectionPosition) => {
    const rawSectionStats = pairedStats[sectionPosition] ?? null;
    const sectionStatsId =
      normalizeIdentifier(rawSectionStats?.id) ?? section.id;
    const questionScores = section.homeworkQuestions.map((question, questionIndex) =>
      createQuestionScore(
        toQuestionStatsArray(
          rawSectionStats?.question_stats ?? rawSectionStats?.questionStats,
        )[questionIndex] ?? null,
        question,
        sectionStatsId,
      ),
    );
    const preLectureQuestionScores = section.preLectureQuestions.map(
      (question, questionIndex) =>
        createQuestionScore(
          toQuestionStatsArray(
            rawSectionStats?.pre_lecture_question_stats ??
              rawSectionStats?.preLectureQuestionStats,
          )[questionIndex] ?? null,
          question,
          sectionStatsId,
        ),
    );

    return {
      homeworkScore: getSectionTotal(
        rawSectionStats?.total_score ?? rawSectionStats?.totalScore,
        questionScores,
      ),
      id: section.id,
      preLectureQuestionScores,
      preLectureScore: getSectionTotal(
        rawSectionStats?.total_pre_lecture_score ??
          rawSectionStats?.totalPreLectureScore,
        preLectureQuestionScores,
      ),
      questionScores,
      sectionStatsId,
    };
  });

  const hasHomeworkScore = sectionScores.some((section) => section.homeworkScore !== null);
  const hasPreLectureScore = sectionScores.some(
    (section) => section.preLectureScore !== null,
  );

  return {
    componentId: assignment.componentId,
    componentTitle: assignment.title,
    componentUid: assignment.componentUid,
    homeworkPossible: assignment.homeworkMaxScore,
    homeworkScore: hasHomeworkScore
      ? sectionScores.reduce(
          (total, section) => total + (section.homeworkScore ?? 0),
          0,
        )
      : null,
    participationPossible: 0,
    participationScore: null,
    preLecturePossible: assignment.preLectureMaxScore,
    preLectureQuestionScores: sectionScores.flatMap(
      (section) => section.preLectureQuestionScores,
    ),
    preLectureScore: hasPreLectureScore
      ? sectionScores.reduce(
          (total, section) => total + (section.preLectureScore ?? 0),
          0,
        )
      : null,
    questionScores: sectionScores.flatMap((section) => section.questionScores),
    sectionScores,
  };
}

export function normalizeGradebookStudent(
  rawStudent: RawCourseStudent,
  assignments: GradebookAssignment[],
  courseId?: string,
): GradebookStudent {
  const baseStudent = createRosterStudent(rawStudent);
  const totals = createEmptyStudentTotals(assignments);
  const rawComponents = toComponentArray(rawStudent.components);
  const assignmentScores = assignments.reduce<Record<string, AssignmentScore>>(
    (nextScores, assignment) => {
      const normalizedScore = normalizeAssignmentScore(
        assignment,
        findRawComponent(rawComponents, assignment) ?? null,
        courseId,
      );

      if (normalizedScore) {
        nextScores[assignment.componentUid] = normalizedScore;
      }

      return nextScores;
    },
    {},
  );

  return {
    ...baseStudent,
    assignmentScores,
    hasLoadedGrades: true,
    totalHomeworkPossible: totals.totalHomeworkPossible,
    totalHomeworkScore: Object.values(assignmentScores).reduce(
      (total, assignment) => total + (assignment.homeworkScore ?? 0),
      0,
    ),
    totalParticipationPossible: totals.totalParticipationPossible,
    totalParticipationScore: Object.values(assignmentScores).reduce(
      (total, assignment) => total + (assignment.participationScore ?? 0),
      0,
    ),
    totalPreLecturePossible: totals.totalPreLecturePossible,
    totalPreLectureScore: Object.values(assignmentScores).reduce(
      (total, assignment) => total + (assignment.preLectureScore ?? 0),
      0,
    ),
  };
}

export function formatScoreCell(
  score: number | null | undefined,
  possible: number,
) {
  if (score === null || score === undefined) {
    return '—';
  }

  return `${formatGradeValue(score)}/${formatGradeValue(possible)}`;
}

/**
 * The SetQuestionScores request for an instructor's edits: one entry per
 * question in editedQuestionScores (the caller passes only the questions whose
 * score changed), addressed as grading addresses a question: its core section,
 * its kind and its position among that section's questions of that kind. The
 * component is named by its uid, which keys the student's record.
 */
export function buildQuestionScoresPayload(
  params: SaveGradeEditParams,
  instructorUserId: string,
) {
  const scores = params.assignment.sections.flatMap((section) =>
    [...section.homeworkQuestions, ...section.preLectureQuestions]
      .filter((question) => buildQuestionMetaKey(question) in params.editedQuestionScores)
      .map((question) => ({
        is_pre_lecture: question.kind === 'preLecture',
        question_index: question.index,
        score: params.editedQuestionScores[buildQuestionMetaKey(question)],
        section_id: question.sectionId,
      })),
  );

  return {
    component_uid: params.assignment.componentUid,
    course_uid: params.courseId,
    scores,
    student_user_id: params.student.id,
    user_id: instructorUserId,
  };
}

/**
 * A question for a class-wide action, addressed as the score save addresses it
 * (section, kind, position), plus its id as this page last saw it. The server
 * refuses a request whose id no longer matches: the chapter changed, and the
 * position may now name another question.
 */
export function buildQuestionRef(question: GradebookQuestionMeta) {
  return {
    is_pre_lecture: question.kind === 'preLecture',
    question_id: question.id,
    question_index: question.index,
    section_id: question.sectionId,
  };
}

export function buildGiveFullCreditPayload(
  params: QuestionClassActionParams,
  instructorUserId: string,
) {
  return {
    component_uid: params.assignment.componentUid,
    course_uid: params.courseId,
    question: buildQuestionRef(params.question),
    user_id: instructorUserId,
  };
}

export function buildSetQuestionRemovedPayload(
  params: SetQuestionRemovedParams,
  instructorUserId: string,
) {
  return {
    ...buildGiveFullCreditPayload(params, instructorUserId),
    removed: params.removed,
  };
}

export function buildCoachScoresPayload(params: SaveCoachScoresParams, instructorUserId: string) {
  return {
    course_uid: params.courseId,
    module_uid: params.assignment.componentUid,
    scores: Object.entries(params.scores).map(([unitUid, score]) => ({
      score,
      unit_uid: unitUid,
    })),
    student_user_id: params.student.id,
    user_id: instructorUserId,
  };
}

export class KuduGradebookService implements GradebookService {
  constructor(
    private readonly apiClient: ApiClient,
    private readonly instructorUserId: string,
  ) {}

  async getRoster(courseId: string): Promise<GradebookRoster> {
    const response = await this.apiClient.invokeKuduMethod<
      | RawGetCourseStudentGroupResponse
      | LegacyKuduResponseEnvelope<RawGetCourseStudentGroupResponse>,
      {
        course_id: string;
        include_student_details: boolean;
        is_instructor_request: boolean;
        user_id: string;
      }
    >('GetCourseStudentGroup', {
      course_id: courseId,
      include_student_details: true,
      is_instructor_request: true,
      user_id: this.instructorUserId,
    });
    const payload = unwrapCourseStudentGroupResponse(response);
    const group =
      payload?.course_student_group ?? payload?.courseStudentGroup ?? null;
    return readRoster(group);
  }

  async getStudentGrades(
    courseId: string,
    startIndex: number,
    endIndex: number,
    componentIds: string[],
    assignments: GradebookAssignment[],
  ): Promise<GradebookStudent[]> {
    const response = await this.apiClient.invokeKuduMethod<
      | RawGetCourseStudentsResponse
      | LegacyKuduResponseEnvelope<RawGetCourseStudentsResponse>,
      {
        component_ids?: string[];
        course_id: string;
        end_index: number;
        start_index: number;
      }
    >('GetCourseStudents', {
      ...(componentIds.length > 0 ? { component_ids: componentIds } : {}),
      course_id: courseId,
      end_index: endIndex,
      start_index: startIndex,
    });
    const payload = unwrapCourseStudentsResponse(response);

    return toCourseStudentArray(
      payload?.course_students ?? payload?.courseStudents,
    ).map((student) => normalizeGradebookStudent(student, assignments, courseId));
  }

  async saveGradeEdit(params: SaveGradeEditParams): Promise<void> {
    const payload = buildQuestionScoresPayload(params, this.instructorUserId);

    await this.apiClient.invokeKuduMethod<unknown, typeof payload>(
      'SetQuestionScores',
      payload,
    );
  }

  async saveCoachUnitScores(params: SaveCoachScoresParams): Promise<void> {
    const payload = buildCoachScoresPayload(params, this.instructorUserId);

    await this.apiClient.invokeKuduMethod<unknown, typeof payload>(
      'SetKaiCoachUnitScores',
      payload,
    );
  }

  async giveQuestionFullCredit(params: QuestionClassActionParams): Promise<void> {
    const payload = buildGiveFullCreditPayload(params, this.instructorUserId);

    await this.apiClient.invokeKuduMethod<unknown, typeof payload>(
      'GiveQuestionFullCredit',
      payload,
    );
  }

  async setQuestionRemoved(params: SetQuestionRemovedParams): Promise<void> {
    const payload = buildSetQuestionRemovedPayload(params, this.instructorUserId);

    await this.apiClient.invokeKuduMethod<unknown, typeof payload>(
      'SetQuestionRemoved',
      payload,
    );
  }
}

export function useKuduGradebookService(): GradebookService | null {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return useMemo(() => {
    if (!user) {
      return null;
    }

    const instructorUserId = getKuduUserId(user);

    if (!instructorUserId) {
      return null;
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const client = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });

    return new KuduGradebookService(client, instructorUserId);
  }, [getIdToken, location.hash, location.pathname, location.search, navigate, user]);
}

export function mergeGradebookStudents(
  existingStudents: GradebookStudent[],
  nextStudents: GradebookStudent[],
): GradebookStudent[] {
  const merged = new Map(existingStudents.map((student) => [student.id, student]));

  nextStudents.forEach((student) => {
    merged.set(student.id, student);
  });

  return [...merged.values()];
}

export function createInitialStudentGradesState(
  total: number,
  batchSize = DEFAULT_GRADEBOOK_BATCH_SIZE,
): StudentGradesState {
  return {
    batchSize,
    isComplete: total === 0,
    loaded: 0,
    students: [],
    total,
  };
}
